import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Pause, Play } from "lucide-react";

const SECTIONS = [
    {
        label: "Verse 1",
        lines: [
            "You left your jacket on my chair",
            "Still smells like cheap perfume and fights",
            "Scrolling through our greatest hits",
            "All those screenshots, petty nights",
            "You said \u201cI'm done, don't text again\u201d",
            "Cool, I wasn't tryna stay",
            "Turned our drama to a playlist",
            "Let it autoplay decay",
        ],
    },
    {
        label: "Chorus",
        lines: [
            "Dead flowers in my inbox",
            "All your words turned grey",
            "I don't even feel it",
            "When your ghost walks through my brain",
            "We were wild, we were toxic",
            "Now you're storming someone else",
            "Dead flowers in my inbox",
            "I just left them there to wilt",
        ],
    },
    {
        label: "Verse 2",
        lines: [
            "I wore the blame like thrift-store clothes",
            "Too big, too small, it never fit",
            "You loved me like a loaded dare",
            "Dared me, then you dipped",
            "You're posting poems for attention",
            "Burning bridges, spelling names",
            "I could answer, I could mention",
            "But I'm tired of that game",
        ],
    },
    {
        label: "Chorus",
        lines: [
            "Dead flowers in my inbox",
            "All your words turned grey",
            "I don't even feel it",
            "When your ghost walks through my brain",
            "We were wild, we were toxic",
            "Now you're storming someone else",
            "Dead flowers in my inbox",
            "I just left them there to wilt",
        ],
    },
    {
        label: "Bridge",
        lines: [
            "I don't hate you, I don't miss you",
            "I just learned to keep my space",
            "You were always chasing chaos",
            "I was always taking blame",
            "Let the past stay cracked and crooked",
            "Let the messages stay sealed",
            "If you're looking for a reaction",
            "Guess apathy's the kill",
        ],
    },
    {
        label: "Final chorus",
        lines: [
            "Dead flowers in my inbox",
            "All your words turned grey",
            "I don't even feel it",
            "When your ghost walks through my brain",
            "We were wild, we were toxic",
            "Now you're storming someone else",
            "Dead flowers in my inbox",
            "And I'm finally doing well",
        ],
    },
];

const Line = ({ children, i }) => (
    <motion.p
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.9, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
    >
        {children}
    </motion.p>
);

export default function LyricsPage({ isPlaying, onPlay }) {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div data-testid="lyrics-page" className="relative min-h-screen">
            <div className="absolute left-1/2 top-0 h-[50vh] w-[60vw] -translate-x-1/2 rounded-full bg-blood/10 blur-[140px]" />

            <header className="relative z-10 flex items-center justify-between px-6 py-6 md:px-16">
                <Link
                    to="/"
                    data-testid="lyrics-back-link"
                    className="group flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-bone"
                >
                    <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
                    bejnni
                </Link>
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-600">
                    Track 01 — 2:53
                </span>
            </header>

            <div className="relative z-10 px-6 pb-40 pt-16 md:px-16 md:pt-24">
                <motion.p
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-6 font-mono text-xs uppercase tracking-[0.35em] text-zinc-500"
                >
                    Lyrics
                </motion.p>
                <div className="overflow-hidden">
                    <motion.h1
                        data-testid="lyrics-title"
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="font-serif text-6xl italic leading-[0.95] tracking-tighter sm:text-7xl lg:text-9xl"
                    >
                        dead flowers
                    </motion.h1>
                </div>

                <motion.button
                    data-testid="lyrics-play-button"
                    onClick={onPlay}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={isPlaying ? "Pause dead flowers" : "Play dead flowers"}
                    className="mt-10 flex w-fit items-center gap-4 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] transition-colors duration-500 hover:border-blood hover:bg-blood/10"
                >
                    {isPlaying ? (
                        <Pause className="h-4 w-4" />
                    ) : (
                        <Play className="h-4 w-4" />
                    )}
                    {isPlaying ? "Pause" : "Play the track"}
                </motion.button>

                <div className="mt-24 md:mt-40">
                    {SECTIONS.map((section, s) => (
                        <div
                            key={section.label + s}
                            data-testid={`lyrics-section-${s}`}
                            className={`mb-24 md:mb-40 ${
                                s % 2 === 1 ? "md:ml-auto md:text-right" : ""
                            } md:max-w-3xl`}
                        >
                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.8 }}
                                className="mb-10 font-mono text-xs uppercase tracking-[0.35em] text-blood"
                            >
                                [ {section.label} ]
                            </motion.p>
                            <div
                                className={`font-serif text-2xl leading-snug tracking-tight sm:text-3xl lg:text-4xl ${
                                    section.label.includes("Chorus") || section.label.includes("chorus")
                                        ? "italic text-bone"
                                        : "text-zinc-400"
                                }`}
                            >
                                {section.lines.map((line, i) => (
                                    <Line key={i} i={i}>
                                        {line}
                                    </Line>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="border-t border-white/10 pt-10"
                >
                    <Link
                        to="/"
                        data-testid="lyrics-footer-back-link"
                        className="group flex w-fit items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 transition-colors duration-300 hover:text-bone"
                    >
                        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
                        Back to the record
                    </Link>
                </motion.div>
            </div>
        </div>
    );
}
